import React from 'react';
import { Card, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useRunBasePrepLambdas,
  useRunBasePrepImports,
  useRunFullRefresh
} from 'api/hooks/use-admin-municipalities';
import AddMunicipalitySectionForm from './AddMunicipalitySectionForm';
import MunicipalitySectionRow from './MunicipalitySectionRow';
import MunicipalityDataUpdates from './MunicipalityDataUpdates';

const MunicipalityPipeline = ({ municipality }) => {
  const municipalityId = municipality.id;

  const { mutate: runLambdas, isLoading: runningLambdas } = useRunBasePrepLambdas({
    municipalityId,
    options: {
      onSuccess: (data) => toast.success(data?.message || 'Base prep lambdas queued'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to queue base prep lambdas')
    }
  });

  const { mutate: runImports, isLoading: runningImports } = useRunBasePrepImports({
    municipalityId,
    options: {
      onSuccess: (data) => toast.success(data?.message || 'Base prep imports queued'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to queue base prep imports')
    }
  });

  const { mutate: runFullRefresh, isLoading: runningFullRefresh } = useRunFullRefresh({
    municipalityId,
    options: {
      onSuccess: (data) => toast.success(data?.message || 'Full refresh queued'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to queue full refresh')
    }
  });

  const handleRunLambdas = () => {
    if (window.confirm(`Run base prep lambdas for ${municipality.name}? This invokes AWS Lambda and may take several minutes.`)) {
      runLambdas();
    }
  };

  const handleRunImports = () => {
    if (window.confirm(`Run base prep imports (Accounts, Departments, Employees, Vendors) for ${municipality.name}?`)) {
      runImports();
    }
  };

  const handleRunFullRefresh = () => {
    if (window.confirm(`Run a full refresh of ALL years of data for ${municipality.name}? This re-runs every lambda and import for the complete history (ignoring the configured years-of-data window) and may take a while.`)) {
      runFullRefresh();
    }
  };

  return (
    <>
      <Card className="mb-4">
        <Card.Header><h5 className="mb-0">Run Base Prep</h5></Card.Header>
        <Card.Body>
          <p className="text-muted">
            Base prep covers the shared lookups every module depends on: account control, departments, employees, vendors, and workflow.
          </p>
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={handleRunLambdas}
              disabled={runningLambdas}
            >
              {runningLambdas ? <Spinner animation="border" size="sm" /> : 'Run Base Prep Lambdas'}
            </Button>
            <Button
              variant="primary"
              onClick={handleRunImports}
              disabled={runningImports}
            >
              {runningImports ? <Spinner animation="border" size="sm" /> : 'Run Base Prep Imports'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><h5 className="mb-0">Full Refresh</h5></Card.Header>
        <Card.Body>
          <p className="text-muted">
            Re-runs the entire pipeline — every base prep and module lambda plus the downstream import — for <strong>all years</strong> of data, ignoring this municipality&apos;s configured years-of-data window (new municipalities default to 2 years). Use this to backfill the complete history in one click. It runs on the lowest-priority queue, so it yields to nightly and interactive work and may take a while to finish.
          </p>
          <Button
            variant="primary"
            onClick={handleRunFullRefresh}
            disabled={runningFullRefresh}
          >
            {runningFullRefresh ? <Spinner animation="border" size="sm" /> : 'Run Full Refresh (All Years)'}
          </Button>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><h5 className="mb-0">Modules</h5></Card.Header>
        <Card.Body>
          <AddMunicipalitySectionForm
            municipalityId={municipalityId}
            assignedSectionIds={(municipality.municipality_sections || []).map((ms) => ms.section_id)}
          />
          {municipality.municipality_sections?.length > 0 ? (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Module</th>
                  <th>System</th>
                  <th className="text-center">Has Workflow</th>
                  <th className="text-center">Has Allocations</th>
                  <th className="text-center">Silence Stale Alerts</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {municipality.municipality_sections.map((ms) => (
                  <MunicipalitySectionRow
                    key={ms.id}
                    municipalityId={municipalityId}
                    municipalitySection={ms}
                  />
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info" className="mb-0">No modules assigned to this municipality.</Alert>
          )}
        </Card.Body>
      </Card>

      <MunicipalityDataUpdates municipalityId={municipalityId} />
    </>
  );
};

export default MunicipalityPipeline;
